<template>
	<Component :is="tag" class="c-limbo-search">
		<slot v-bind="bindings"></slot>
	</Component>
</template>

<script>
import { CancelToken, isCancel } from 'axios';

let clientFetchKeyCounter = 0;
const hydratedFetchKeys = new Set();
const clone = (value) => JSON.parse(JSON.stringify(value));
const defaultLimit = 12;
const defaultSearchData = {
	data: null,
	facets: null,
	pagination: null,
	misc: null,
	meta: null,
	error: null,
};
const defaultDataMergerMethod = (newData, oldData) =>
	Array.isArray(newData) && Array.isArray(oldData)
		? [...oldData, ...newData]
		: newData;
const defaultGroupedDataMergerMethod = (newData, oldData) => {
	if (!newData?.groups || !oldData?.groups) {
		return newData;
	}
	const groups = oldData.groups.map((group) => {
		const next = newData.groups.find((item) => item.id === group.id);
		return next
			? { ...next, items: [...group.items, ...next.items] }
			: group;
	});
	groups.push(
		...newData.groups.filter(
			(group) => !oldData.groups.some((item) => item.id === group.id)
		)
	);
	return { ...newData, groups };
};
const defaultConfig = {
	callMethod: 'GET',
	enableLiveSearch: false,
	immediateSearch: { useUrlQuery: true, ssr: true },
	limit: { initial: defaultLimit, value: defaultLimit },
	enableGroupedSearch: false,
	groupParameter: 'groups',
	updateUrlQueryOnSearch: true,
	updateVueRouteOnSearch: false,
	clearRouterHashOnSearch: false,
	clearSearchDataOnError: true,
	allowSearchFiltersMutation: false,
	updateSearchFiltersOnBindingChange: true,
	persistentParameters: ['contextId'],
	hiddenParameters: ['siteId', 'contextId', 'pageId', 'cultureId'],
	defaultParameterValues: {},
	searchResponseTransformerMethod: (response) => response,
	searchBodyTransformerMethod: (parameters) => parameters,
	dataMergerMethod: defaultDataMergerMethod,
	dataOutputTransformerMethod: (data) => data,
	searchDelay: 0,
	fetchOptions: {},
	onInit: null,
	onAfterSearch: null,
	onError: null,
};

export default {
	name: 'LimboSearch',
	props: {
		tag: { type: String, default: 'div' },
		searchFilters: {
			type: Object,
			required: true,
			validator: (value) =>
				typeof value.endpointUrl === 'string' &&
				Array.isArray(value.fields),
		},
		extraParameters: { type: Object, default: () => ({}) },
		parameterOverwrites: { type: Object, default: () => ({}) },
		config: { type: Object, default: () => ({}) },
		searchKey: { type: String, default: '' },
	},
	data() {
		return {
			lastRequestedUrl: null,
			searchData: { ...defaultSearchData },
			internalSearchFilters: this.removeReservedParameters(
				this.searchFilters
			),
			searchFiltersClone: this.removeReservedParameters(
				clone(this.searchFilters)
			),
			internalExtraParameters: {},
			internalPagination: {},
			state: {
				isInitiated: false,
				isLoading: false,
				hasFetchedOnce: false,
				hasMoreItems: null,
				isAppend: false,
				isUpdated: false,
			},
			query: { parameters: null },
			latestResponse: null,
		};
	},
	fetchKey(getCounter) {
		return this.searchKey
			? `LimboSearch:key:${this.searchKey}`
			: `LimboSearch:auto:${getCounter('LimboSearch')}`;
	},
	fetchOnServer() {
		return (
			!!this.compConfig.immediateSearch &&
			this.compConfig.immediateSearch.ssr !== false
		);
	},
	async fetch() {
		if (this.compConfig.immediateSearch) {
			await this.requestSearch({
				delay: 0,
				syncUrl: process.client,
			});
		}
	},
	computed: {
		compConfig() {
			const config = { ...defaultConfig, ...this.config };
			if (
				config.enableGroupedSearch &&
				config.dataMergerMethod === defaultDataMergerMethod
			) {
				config.dataMergerMethod = defaultGroupedDataMergerMethod;
			}
			return config;
		},
		bindings() {
			return {
				state: this.state,
				query: this.query,
				...this.searchData,
				data:
					this.searchData.data == null
						? null
						: this.compConfig.dataOutputTransformerMethod(
								this.searchData.data,
								this.latestResponse
						  ),
				lastRequestedUrl: this.lastRequestedUrl,
				latestResponse: this.latestResponse,
				action: {
					submit: this.submit,
					submitWithLimit: this.submitWithLimit,
					fetchMore: this.compConfig.enableGroupedSearch
						? this.fetchMoreGroup
						: this.fetchMore,
					fetchMoreAsync: this.fetchMoreAsync,
					fetchAll: this.compConfig.enableGroupedSearch
						? this.fetchAllGroup
						: this.fetchAll,
					fetchAllAsync: this.fetchAllAsync,
					requestSearch: this.requestSearch,
					setUrlQuery: this.setUrlQuery,
					resetPagination: this.resetPagination,
					resetState: this.resetState,
					getSerializedParams: this.getSerializedParams,
				},
			};
		},
		filters() {
			return this.compConfig.allowSearchFiltersMutation
				? this.internalSearchFilters
				: this.searchFiltersClone;
		},
		endpointUrl() {
			return this.filters.endpointUrl;
		},
		fields() {
			return this.filters.fields;
		},
		searchFilterParameters() {
			return this.fields.reduce((parameters, field) => {
				if (field.name) {
					if ('checked' in field) {
						if (field.checked) {
							parameters[field.name] = field.value;
						}
					} else if (Array.isArray(field.value)) {
						const selected = field.value.find(
							(item) => item.checked
						);
						if (selected) {
							parameters[field.name] = selected.value;
						}
					} else {
						parameters[field.name] = field.value;
					}
				}
				return parameters;
			}, {});
		},
		pagination() {
			return this.convertToParameterStyledPagination(
				this.internalPagination
			);
		},
		watchedParameters() {
			return {
				...this.extraParameters,
				...this.searchFilterParameters,
				...this.parameterOverwrites,
			};
		},
		parameters() {
			const parameters = {
				...this.extraParameters,
				...this.internalExtraParameters,
				...this.searchFilterParameters,
				...this.pagination,
				...this.parameterOverwrites,
			};
			for (const key in parameters) {
				if (
					parameters[key] == null &&
					!this.compConfig.persistentParameters.includes(key)
				) {
					delete parameters[key];
				}
			}
			return parameters;
		},
	},
	watch: {
		searchFilters: {
			deep: true,
			handler(value) {
				if (this.compConfig.updateSearchFiltersOnBindingChange) {
					this.internalSearchFilters =
						this.removeReservedParameters(value);
					this.searchFiltersClone = this.removeReservedParameters(
						clone(value)
					);
				}
			},
		},
		watchedParameters: {
			deep: true,
			handler(value, oldValue) {
				const live = this.compConfig.enableLiveSearch;
				if (
					!process.client ||
					!this._isMounted ||
					!this.state.hasFetchedOnce ||
					!live ||
					this._restoringFilters
				) {
					return;
				}
				const changed = Array.isArray(live)
					? live.some(
							(key) =>
								JSON.stringify(value[key]) !==
								JSON.stringify(oldValue[key])
					  )
					: JSON.stringify(value) !== JSON.stringify(oldValue);
				if (changed) {
					this.submit();
				}
			},
		},
	},
	created() {
		// Transport objects must not enter Nuxt's serialized component data.
		this._searchAxios = this.$axios.create();
		this._requestId = 0;
		this._cancelRequest = null;
		this._pendingSearch = null;
		this._restoringFilters = false;
		this._isMounted = false;
		this.resetPagination();
		if (this.compConfig.immediateSearch?.useUrlQuery) {
			this.mixParametersFromUrl();
			for (const pagination of this.compConfig.enableGroupedSearch
				? Object.values(this.internalPagination)
				: [this.internalPagination]) {
				pagination.limit += pagination.offset;
				pagination.offset = 0;
			}
		}
		this.state.isInitiated = true;
	},
	beforeMount() {
		const fallbackFetchKey = this.searchKey
			? `LimboSearch:key:${this.searchKey}`
			: `LimboSearch:auto:${clientFetchKeyCounter++}`;
		const fetchKey =
			this.$el?.dataset?.fetchKey ||
			this.$vnode?.elm?.dataset?.fetchKey ||
			this._fetchKey ||
			fallbackFetchKey;
		const fetchData = window.__NUXT__?.fetch?.[fetchKey];
		if (this._hydrated) {
			if (fetchData) {
				hydratedFetchKeys.add(fetchKey);
			}
			return;
		}
		if (!fetchData || hydratedFetchKeys.has(fetchKey)) {
			return;
		}
		hydratedFetchKeys.add(fetchKey);
		this._fetchKey = fetchKey;
		this._hydrated = true;
		if (fetchData._error) {
			this.$fetchState.error = fetchData._error;
			return;
		}
		for (const key in fetchData) {
			this.$set(this.$data, key, fetchData[key]);
		}
	},
	mounted() {
		// Nuxt restores local filters from the payload, not their parent props.
		if (this.compConfig.allowSearchFiltersMutation) {
			this._restoringFilters = true;
			for (const field of this.filters.fields) {
				const target = this.searchFilters.fields.find(
					(item) => item.name === field.name
				);
				if (target) {
					this.$set(target, 'value', clone(field.value ?? ''));
					if ('checked' in field) {
						this.$set(target, 'checked', field.checked);
					}
				}
			}
			this.internalSearchFilters = this.removeReservedParameters(
				this.searchFilters
			);
			this.$nextTick(() => {
				this._restoringFilters = false;
			});
		}
		this.$emit('init', true);
		this.compConfig.onInit?.(this);
		this.emitUpdate();
		if (
			this.state.hasFetchedOnce &&
			this.compConfig.clearRouterHashOnSearch?.includeInitialSearch
		) {
			this.setUrlQuery(
				this.getSerializedParams(this.query.parameters),
				true
			);
		}
		this.$nextTick(() => {
			this._isMounted = true;
		});
	},
	beforeDestroy() {
		this.cancelPendingSearch();
	},
	methods: {
		emitUpdate() {
			// Preserve the legacy event payload (functions are slot-only).
			this.$emit('update', clone(this.bindings));
		},
		cancelPendingSearch() {
			this._requestId++;
			this._cancelRequest?.cancel('Search superseded or destroyed');
			this._cancelRequest = null;
			if (this._pendingSearch) {
				clearTimeout(this._pendingSearch.timer);
				this._pendingSearch.resolve();
				this._pendingSearch = null;
			}
		},
		submit() {
			this.internalExtraParameters = {};
			this.resetPagination();
			return this.requestSearch();
		},
		submitWithLimit(limit) {
			this.internalExtraParameters = {};
			this.resetPagination();
			if (this.compConfig.enableGroupedSearch) {
				for (const key in this.internalPagination) {
					this.$set(this.internalPagination[key], 'limit', limit);
				}
			} else {
				this.$set(this.internalPagination, 'limit', limit);
			}
			return this.requestSearch();
		},
		async fetchMore(amount = this.getLimit(null, false)) {
			if (!this.searchData.error && this.state.hasMoreItems) {
				const { offset, limit } = this.searchData.pagination;
				this.internalPagination = {
					offset: +offset + +limit,
					limit: +amount,
				};
				return await this.requestSearch({ append: true });
			}
		},
		async fetchMoreGroup(id, amount = this.getLimit(id, false)) {
			if (!this.searchData.error && this.state.hasMoreItems?.[id]) {
				const { offset, limit } = this.searchData.pagination[id];
				this.$set(
					this.internalExtraParameters,
					this.compConfig.groupParameter,
					id
				);
				this.$set(this.internalPagination, id, {
					offset: +offset + +limit,
					limit: +amount,
				});
				return await this.requestSearch({ append: true });
			}
		},
		async fetchAll() {
			if (!this.searchData.error && this.state.hasMoreItems) {
				const { offset, limit, total } = this.searchData.pagination;
				return await this.fetchMore(+total - +offset - +limit);
			}
		},
		async fetchAllGroup(id) {
			if (!this.searchData.error && this.state.hasMoreItems?.[id]) {
				const { offset, limit, total } = this.searchData.pagination[id];
				return await this.fetchMoreGroup(id, +total - +offset - +limit);
			}
		},
		fetchMoreAsync(...args) {
			return this.compConfig.enableGroupedSearch
				? this.fetchMoreGroup(...args)
				: this.fetchMore(...args);
		},
		fetchAllAsync(...args) {
			return this.compConfig.enableGroupedSearch
				? this.fetchAllGroup(...args)
				: this.fetchAll(...args);
		},
		async requestSearch(options = {}) {
			const {
				append = false,
				delay = this.compConfig.searchDelay,
				syncUrl = true,
				parameterOverwrites = {},
			} = options;
			this.cancelPendingSearch();
			const requestId = this._requestId;
			const params = clone({
				...(append
					? {
							...this.query.parameters,
							...this.internalExtraParameters,
							...this.pagination,
					  }
					: this.parameters),
				...parameterOverwrites,
				...this.parameterOverwrites,
			});
			const hadFetched = this.state.hasFetchedOnce;
			this.state.isInitiated = true;
			this.state.isLoading = true;
			this.state.isAppend = append;
			this.state.isUpdated = false;
			this.emitUpdate();
			if (process.client && delay > 0) {
				await new Promise((resolve) => {
					this._pendingSearch = {
						resolve,
						timer: setTimeout(resolve, delay),
					};
				});
			}
			if (requestId !== this._requestId) {
				return;
			}
			this._pendingSearch = null;
			const source = CancelToken.source();
			this._cancelRequest = source;
			try {
				const method = this.compConfig.callMethod.toUpperCase();
				if (!['GET', 'POST'].includes(method)) {
					throw new Error(`Unsupported search method: ${method}`);
				}
				const serializedParams = this.getSerializedParams(params);
				if (process.client && syncUrl) {
					const clearHash =
						!append &&
						!!this.compConfig.clearRouterHashOnSearch &&
						(hadFetched ||
							!this.compConfig.immediateSearch ||
							this.compConfig.clearRouterHashOnSearch
								.includeInitialSearch);
					this.setUrlQuery(serializedParams, clearHash);
				}
				this.lastRequestedUrl =
					method === 'POST'
						? this.endpointUrl
						: this.appendQuery(this.endpointUrl, serializedParams);
				const response = await this._searchAxios.request({
					...this.compConfig.fetchOptions,
					...(process.server &&
					this.$config?.API_DOMAIN &&
					!this.compConfig.fetchOptions.baseURL
						? { baseURL: this.$config.API_DOMAIN }
						: {}),
					method,
					url: this.lastRequestedUrl,
					data:
						method === 'POST'
							? this.compConfig.searchBodyTransformerMethod(
									params
							  )
							: undefined,
					cancelToken: source.token,
				});
				if (requestId !== this._requestId) {
					return;
				}
				// Keep the legacy Axios transformer contract, but serialize no
				// request/config objects (they can contain sockets and cycles).
				const transformed =
					this.compConfig.searchResponseTransformerMethod(response) ??
					response;
				const payload = transformed.data;
				if (
					!payload ||
					(this.compConfig.enableGroupedSearch
						? !Array.isArray(payload.groups)
						: !('data' in payload))
				) {
					throw new Error('Invalid search response');
				}
				const nextData = this.compConfig.enableGroupedSearch
					? payload
					: payload.data;
				const previousData = JSON.stringify(this.searchData.data);
				this.searchData = {
					data: append
						? this.compConfig.dataMergerMethod(
								nextData,
								clone(this.searchData.data)
						  )
						: nextData,
					facets: payload.facets ?? null,
					meta: payload.meta ?? null,
					misc: payload.misc ?? null,
					error: null,
					pagination: this.compConfig.enableGroupedSearch
						? {
								...(append ? this.searchData.pagination : {}),
								...payload.pagination,
						  }
						: {
								limit: 0,
								offset: 0,
								total: 0,
								...payload.pagination,
						  },
				};
				if (this.compConfig.enableGroupedSearch) {
					for (const group of payload.groups) {
						this.$set(this.searchData.pagination, group.id, {
							limit: +group.limit || 0,
							offset: +group.offset || 0,
							total: +group.total || 0,
						});
					}
					this.state.hasMoreItems = {};
					for (const key in this.searchData.pagination) {
						const page = this.searchData.pagination[key];
						this.$set(this.internalPagination, key, { ...page });
						this.$set(
							this.state.hasMoreItems,
							key,
							+page.offset + +page.limit < +page.total
						);
					}
				} else {
					const page = this.searchData.pagination;
					this.internalPagination = {
						limit: +page.limit,
						offset: +page.offset,
					};
					this.state.hasMoreItems =
						+page.offset + +page.limit < +page.total;
				}
				this.query.parameters = params;
				this.latestResponse = {
					data: payload,
					status: transformed.status ?? response.status,
					statusText: transformed.statusText ?? response.statusText,
				};
				this.state.isUpdated =
					previousData !== JSON.stringify(this.searchData.data);
			} catch (error) {
				if (requestId !== this._requestId || isCancel(error)) {
					return;
				}
				if (this.compConfig.clearSearchDataOnError) {
					this.searchData = { ...defaultSearchData };
					this.latestResponse = null;
				}
				this.state.hasMoreItems = this.compConfig.enableGroupedSearch
					? {}
					: false;
				this.searchData.error = {
					status: error.response?.status ?? null,
					data: error.response?.data ?? null,
					message: error.message,
				};
				console.error('[LimboSearch] Search failed:', error.message);
				this.$emit('error', this.searchData.error);
				this.compConfig.onError?.(this.searchData.error);
			} finally {
				if (requestId === this._requestId) {
					this._cancelRequest = null;
					this.state.hasFetchedOnce = true;
					this.state.isLoading = false;
					this.emitUpdate();
					this.compConfig.onAfterSearch?.(
						clone(this.searchData),
						clone(this.state)
					);
				}
			}
			return this.bindings;
		},
		appendQuery(url, query) {
			const [path, hash] = url.split('#');
			return (
				path +
				(query ? `${path.includes('?') ? '&' : '?'}${query}` : '') +
				(hash ? `#${hash}` : '')
			);
		},
		getSerializedParams(parameters = this.parameters) {
			return Object.entries(parameters)
				.filter(
					([key, value]) =>
						this.compConfig.persistentParameters.includes(key) ||
						(value != null &&
							(value !== '' ||
								(key in
									this.compConfig.defaultParameterValues &&
									value !==
										this.compConfig.defaultParameterValues[
											key
										])))
				)
				.map(
					([key, value]) =>
						`${encodeURIComponent(key)}=${encodeURIComponent(
							Array.isArray(value) ? value.join(',') : value ?? ''
						)}`
				)
				.join('&');
		},
		mixParametersFromUrl() {
			for (const [key, rawValue] of Object.entries(this.$route.query)) {
				if (this.compConfig.hiddenParameters.includes(key)) {
					continue;
				}
				const value = Array.isArray(rawValue)
					? rawValue.join(',')
					: rawValue;
				const groupPage = this.compConfig.enableGroupedSearch
					? /^([lo])(\d+)$/.exec(key)
					: null;
				if (
					groupPage ||
					(!this.compConfig.enableGroupedSearch &&
						['limit', 'offset'].includes(key))
				) {
					const number = Number(value);
					if (!Number.isSafeInteger(number) || number < 0) {
						continue;
					}
					const page = groupPage
						? this.internalPagination[groupPage[2]] || {
								limit: this.getLimit(groupPage[2]),
								offset: 0,
						  }
						: this.internalPagination;
					this.$set(
						page,
						groupPage
							? groupPage[1] === 'l'
								? 'limit'
								: 'offset'
							: key,
						number
					);
					if (groupPage) {
						this.$set(this.internalPagination, groupPage[2], page);
					}
				} else if (!this.setSearchFilterField(key, value)) {
					this.$set(this.internalExtraParameters, key, value);
				}
			}
		},
		setSearchFilterField(key, value) {
			const field = this.fields.find((item) => item.name === key);
			if (!field) {
				return false;
			}
			if (Array.isArray(field.value)) {
				if (field.value.some((item) => String(item.value) === value)) {
					for (const item of field.value) {
						this.$set(
							item,
							'checked',
							String(item.value) === value
						);
					}
				}
			} else if ('checked' in field) {
				this.$set(field, 'checked', String(field.value) === value);
			} else {
				this.$set(field, 'value', value ?? '');
			}
			return true;
		},
		getLimit(id, initial = true) {
			const limit = this.compConfig.enableGroupedSearch
				? this.compConfig.limit[id]
				: this.compConfig.limit;
			return (
				(initial ? limit?.initial : null) ??
				limit?.value ??
				(typeof limit === 'number' ? limit : defaultLimit)
			);
		},
		resetPagination() {
			if (this.compConfig.enableGroupedSearch) {
				const keys = new Set([
					...Object.keys(this.internalPagination),
					...Object.keys(this.compConfig.limit).filter((key) =>
						/^\d+$/.test(key)
					),
				]);
				this.internalPagination = {};
				for (const key of keys) {
					this.$set(this.internalPagination, key, {
						limit: this.getLimit(key),
						offset: 0,
					});
				}
			} else {
				this.internalPagination = {
					limit: this.getLimit(),
					offset: 0,
				};
			}
		},
		resetState() {
			this.cancelPendingSearch();
			this.state = {
				isInitiated: true,
				isLoading: false,
				hasFetchedOnce: false,
				hasMoreItems: null,
				isAppend: false,
				isUpdated: false,
			};
		},
		setUrlQuery(query = this.getSerializedParams(), clearHash = false) {
			if (
				!process.client ||
				(!this.compConfig.updateUrlQueryOnSearch &&
					!this.compConfig.updateVueRouteOnSearch)
			) {
				return;
			}
			const parameters = new URLSearchParams(query);
			const defaults = {
				limit: this.getLimit(),
				offset: 0,
				...this.compConfig.defaultParameterValues,
			};
			for (const [key, value] of [...parameters.entries()]) {
				const groupPage = /^([lo])(\d+)$/.exec(key);
				const defaultValue =
					key in defaults
						? defaults[key]
						: groupPage
						? groupPage[1] === 'l'
							? this.getLimit(groupPage[2])
							: 0
						: undefined;
				const hideGroup =
					this.compConfig.enableGroupedSearch &&
					key === this.compConfig.groupParameter &&
					key in this.internalExtraParameters &&
					!(key in this.searchFilterParameters) &&
					!(key in this.extraParameters) &&
					!(key in this.parameterOverwrites);
				if (
					hideGroup ||
					this.compConfig.hiddenParameters.includes(key) ||
					(defaultValue !== undefined &&
						String(defaultValue) === value)
				) {
					parameters.delete(key);
				}
			}
			const url =
				this.$route.path +
				(parameters.toString() ? `?${parameters}` : '') +
				(clearHash ? '' : window.location.hash);
			if (this.compConfig.updateVueRouteOnSearch) {
				if (this.$route.fullPath !== url) {
					const navigation = this.compConfig.updateVueRouteOnSearch
						.pushHistory
						? this.$router.push(url)
						: this.$router.replace(url);
					navigation?.catch((error) => {
						if (error.name !== 'NavigationDuplicated') {
							console.error(
								'[LimboSearch] URL update failed:',
								error
							);
							this.$emit('error', {
								message: error.message,
							});
						}
					});
				}
			} else {
				window.history.replaceState(window.history.state, '', url);
			}
		},
		removeReservedParameters(filters) {
			return {
				...filters,
				fields: (filters.fields || []).filter(
					(field) =>
						!['limit', 'offset', 'total'].includes(
							field.name?.toLowerCase()
						)
				),
			};
		},
		convertToParameterStyledPagination(pagination) {
			if (!this.compConfig.enableGroupedSearch) {
				return { ...pagination };
			}
			return Object.entries(pagination).reduce(
				(parameters, [key, value]) => ({
					...parameters,
					[`l${key}`]: value.limit,
					[`o${key}`]: value.offset,
				}),
				{}
			);
		},
	},
};
</script>
